import type { SceneDocument } from '../../types/scene';
import { sanitizeAnimatedGifPlacements } from './sceneAnimatedGifHelpers';
import { externalizeSceneAssetUrls } from './sceneAssetUrls';

type PersistableSceneAssetInput = Pick<
  SceneDocument,
  | 'animatedGifs'
  | 'backgroundUrl'
  | 'foregroundUrl'
  | 'levelDesignId'
  | 'thumbnailUrl'
  | 'traversalMaskUrl'
>;

type PersistedSceneAssets = Pick<
  SceneDocument,
  'animatedGifs' | 'backgroundUrl' | 'foregroundUrl' | 'thumbnailUrl' | 'traversalMaskUrl'
>;

const toInlineSceneAssetRefs = (input: PersistableSceneAssetInput): PersistedSceneAssets => ({
  animatedGifs: sanitizeAnimatedGifPlacements(input.animatedGifs),
  backgroundUrl: input.backgroundUrl?.trim() || null,
  foregroundUrl: input.foregroundUrl?.trim() || null,
  thumbnailUrl: input.thumbnailUrl?.trim() || null,
  traversalMaskUrl: input.traversalMaskUrl?.trim() || null,
});

const isInlineDataUrl = (value: string | null | undefined): boolean =>
  Boolean(value?.trim()) && value!.trim().startsWith('data:');

export const hasInlineSceneBinaryAssetData = (
  input: Pick<
    PersistableSceneAssetInput,
    'backgroundUrl' | 'foregroundUrl' | 'thumbnailUrl' | 'traversalMaskUrl'
  >,
): boolean =>
  isInlineDataUrl(input.backgroundUrl) ||
  isInlineDataUrl(input.foregroundUrl) ||
  isInlineDataUrl(input.thumbnailUrl) ||
  isInlineDataUrl(input.traversalMaskUrl);

export const shouldUseStorageAssetPromotionForSave = (
  input: Pick<
    PersistableSceneAssetInput,
    'backgroundUrl' | 'foregroundUrl' | 'levelDesignId' | 'thumbnailUrl' | 'traversalMaskUrl'
  >,
): boolean => !input.levelDesignId.trim() || hasInlineSceneBinaryAssetData(input);

export const shouldPersistSceneAssetsForSave = (
  input: Pick<
    PersistableSceneAssetInput,
    'backgroundUrl' | 'foregroundUrl' | 'levelDesignId' | 'thumbnailUrl' | 'traversalMaskUrl'
  >,
): boolean => shouldUseStorageAssetPromotionForSave(input);

export const persistSceneAssetsForSave = async ({
  input,
  sceneId,
}: {
  input: PersistableSceneAssetInput;
  ownerId: string;
  sceneId: string;
}): Promise<PersistedSceneAssets> => {
  if (shouldUseStorageAssetPromotionForSave(input)) {
    return externalizeSceneAssetUrls(sceneId, input);
  }
  return toInlineSceneAssetRefs(input);
};
